import { Location } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { AlertController, ModalController, NavController, PopoverController } from '@ionic/angular';
import { ColumnMode } from '@swimlane/ngx-datatable';
import moment from 'moment';
import { Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';

import { ComposeComponentOptions, ComposePage } from 'src/app/components/compose/compose.page';
import { OUTBOUND_STATUS, routes } from 'src/app/constants';
import { removeUndefinedFields } from 'src/app/utils/utils';
import { ListQueryResult } from 'src/models';
import { AnalyticsService } from 'src/services/analytics.service';
import { ApiService } from 'src/services/api.service';
import { AuthService } from 'src/services/auth.service';
import { MessageService } from 'src/services/message.service';
import { UsersService } from 'src/services/users.service';

import { TextPopoverComponent } from '../text-popover/text-popover.component';

import { MessageAnalyticsPage } from './message-analytics/message-analytics.page';

@Component({
  selector: 'app-messaging',
  templateUrl: './messaging.component.html',
  styleUrls: ['./messaging.component.scss'],
})
export class MessagingComponent implements OnInit, OnDestroy {
  selected = [];
  messages = [];
  filter = 'all';
  isLoading = false;
  ColumnMode = ColumnMode;
  routerSub: Subscription;
  hasAllSlackScopes = false;
  pendingOutbound: string;

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private location: Location,
    private apiService: ApiService,
    private msgSrvc: MessageService,
    private modalCtrl: ModalController,
    private alertCtrl: AlertController,
    private popoverCtrl: PopoverController,
    private navController: NavController,
    private usersService: UsersService,
    private authService: AuthService,
    private analyticsService: AnalyticsService,
  ) {}

  async ngOnInit() {
    const currentPath = this.location.path();

    this.routerSub = this.router.events
      .pipe(filter((e) => e instanceof NavigationEnd && currentPath === e.url))
      .subscribe(() => {
        this.getOutboundList();
      });

    this.pendingOutbound = this.route.snapshot.queryParamMap.get('outboundId');

    this.getOutboundList();
  }

  ngOnDestroy() {
    this.routerSub.unsubscribe();
  }

  async getOutboundList() {
    const queryParams: any = {};

    if (this.filter !== 'all') {
      queryParams.status = this.filter;
    }

    if (this.authService.selectedProject) {
      queryParams.projectId = this.authService.selectedProject.id;
    }

    this.isLoading = true;

    try {
      const result: ListQueryResult = await this.apiService.getPromise(`/outbounds`, queryParams);

      this.messages = result.rows.map((outbound) => {
        if (this.pendingOutbound && this.pendingOutbound === outbound.id) {
          this.viewAnalytics(outbound);
          this.pendingOutbound = undefined;
        }

        let groupCount = 0;
        let peopleCount = 0;

        if (outbound.deliveryMethod === 'email') {
          outbound.recipients?.forEach((r) => {
            if (r.count) {
              peopleCount += r.count;
              groupCount++;
            } else {
              peopleCount++;
            }
          });
        } else {
          outbound.recipients?.forEach((r) => {
            peopleCount += r.followerCount ?? 0;
            groupCount++;
          });
          groupCount = outbound.recipients?.length;
        }

        return {
          ...outbound,
          sendAt: new Date(outbound.runAt ?? outbound.createdAt),
          groupCount,
          peopleCount,
        };
      });

      // HACK: Fix for issue where message would be marked as "sending" until the page is refreshed. Check if there are any very recently created messages
      // that are "sending" and refresh the messages. Will only attempt for an hour after the message was created so we don't end up in situation where
      // we refresh forever waiting on an actually stuck message.
      if (
        this.messages.some(
          (m) =>
            m.status === OUTBOUND_STATUS.SCHEDULED &&
            !m.runAt &&
            moment(m.createdAt).isAfter(moment().subtract(1, 'hours')),
        )
      ) {
        setTimeout(() => {
          this.getOutboundList();
        }, 1000);
      }
    } catch (err) {
      this.msgSrvc.showError(err);
    } finally {
      this.isLoading = false;
    }
  }

  async getUsersForTenant() {
    const res = await this.usersService.list();

    return res.data;
  }

  async newEmailMessage() {
    const modal = await this.modalCtrl.create({
      component: ComposePage,
      componentProps: {
        emails: [],
        showTemplates: true,
        callback: () => {
          this.getOutboundList();
        },
      },
      backdropDismiss: false,
    });
    modal.present();
  }

  async editNotes(outbound: any) {
    const popover = await this.popoverCtrl.create({
      component: TextPopoverComponent,
      componentProps: {
        text: outbound.notes,
        shouldSelect: true,
        isMultiline: true,
        callback: async (text: string) => {
          this.isLoading = true;

          const request = this.apiService.put(`/outbounds/${outbound.id}`, {
            ...outbound,
            notes: text,
          });

          request.subscribe(
            async (_result) => {
              this.isLoading = false;
              outbound.notes = text;
            },
            async (err) => {
              this.msgSrvc.show(err.message || "That didn't work, please try again.");
              this.isLoading = false;
            },
          );
        },
      },
      showBackdrop: false,
      event,
    });
    popover.present();
  }

  async viewAnalytics(outbound: any) {
    this.analyticsService.trackEvent('Messaging', 'View outbound analytics', {
      outboundId: outbound.id,
      deliveryMethod: outbound.deliveryMethod,
    });

    const modal = await this.modalCtrl.create({
      component: MessageAnalyticsPage,
      componentProps: {
        outbound,
      },
      cssClass: 'modal-large',
    });

    modal.present();
  }

  async editMessage(outbound: any) {
    if (outbound.deliveryMethod === 'email') {
      this.editEmailMessage(outbound);
    } else {
      this.editSlackMessage(outbound);
    }
  }

  async editEmailMessage(outbound: any) {
    const options: ComposeComponentOptions = {
      title:
        outbound.status === OUTBOUND_STATUS.DRAFT
          ? 'Compose email'
          : outbound.status === OUTBOUND_STATUS.SCHEDULED
            ? 'Edit scheduled email'
            : 'Review sent email',
      hideEmails: false,
    };

    const modal = await this.modalCtrl.create({
      component: ComposePage,
      componentProps: {
        showTemplates: outbound.status === 'draft',
        options,
        draft: outbound,
        callback: () => {
          this.getOutboundList();
        },
      },
      backdropDismiss: false,
      cssClass: 'modal-large',
    });

    modal.present();
  }

  async newSlackMessage() {
    this.navController.navigateForward(`${routes.SLACK_MESSAGE}/new`, {
      queryParams: removeUndefinedFields({
        previousUrl: this.location.path(),
        customerIds: [],
      }),
    });
  }

  async editSlackMessage(outbound: any) {
    this.navController.navigateForward(`${routes.SLACK_MESSAGE}/${outbound.id}`, {
      queryParams: {
        previousUrl: this.location.path(),
      },
    });
  }

  viewTemplates() {
    this.navController.navigateRoot(`${routes.DASHBOARD}/${routes.OUTBOUND}/templates`);
  }

  async showError(outbound: any) {
    if (!outbound.error) {
      return;
    }

    const name = `Failed to send outbound`;
    const note = `"${outbound.error}"`;
    const alert = await this.alertCtrl.create({
      header: name,
      subHeader: note,
    });

    alert.present();
  }
}
